import React, { useEffect, useMemo, useState } from "react";
import { useBdQuery } from "../hooks/useBdApi";
import { TrendChart } from "./Charts";
import { Card, Skeleton } from "./Ui";
import { TitleWithSource } from "./SourceTip";
import { PRESETS, rangeForPreset, todayEt } from "../hooks/usePeriod";
import { formatDate } from "../metrics";

// Widget-level range for the "Activity over time" chart. The chart follows
// the page period until the viewer picks a preset or custom range here, at
// which point it fetches its own series from GET /trend (same scope and date
// basis as the page) while the rest of the page stays on the page period.
const FOLLOW_PAGE = "page";
const isIso = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ""));

export default function TrendCard({
  period,
  pageTrend,
  pageGranularity,
  loading,
  scope = "org",
  team,
  user,
  subtitle,
  dataWindow = null,
  height = 280,
}) {
  const [preset, setPreset] = useState(FOLLOW_PAGE);
  const [draft, setDraft] = useState({ from: period.range.from, to: period.range.to });
  const [custom, setCustom] = useState(null);

  // keep the custom inputs seeded from the page period while following it
  useEffect(() => {
    if (preset === FOLLOW_PAGE) setDraft({ from: period.range.from, to: period.range.to });
  }, [preset, period.range.from, period.range.to]);

  const ownRange = useMemo(() => {
    if (preset === FOLLOW_PAGE) return null;
    if (preset === "custom") return custom;
    const r = rangeForPreset(preset, todayEt(), dataWindow);
    return { from: r.from.toISODate(), to: r.to.toISODate() };
  }, [preset, custom, dataWindow]);

  const params = useMemo(
    () =>
      ownRange
        ? {
            scope,
            ...(team ? { team } : {}),
            ...(user ? { user } : {}),
            from: ownRange.from,
            to: ownRange.to,
            ...(period.dateBasis !== "booked" ? { dateBasis: period.dateBasis } : {}),
          }
        : null,
    [ownRange, scope, team, user, period.dateBasis]
  );
  const own = useBdQuery("trend", params || {}, { enabled: !!params });

  const usingOwn = !!ownRange;
  const trend = usingOwn ? (own.data && own.data.trend) || [] : pageTrend || [];
  const granularity = usingOwn ? own.data && own.data.period && own.data.period.granularity : pageGranularity;
  const busy = usingOwn ? own.loading && !own.data : loading;
  const range = ownRange || period.range;

  const applyCustom = () => {
    if (!isIso(draft.from) || !isIso(draft.to)) return;
    const [a, b] = draft.from <= draft.to ? [draft.from, draft.to] : [draft.to, draft.from];
    setCustom({ from: a, to: b });
    setPreset("custom");
  };

  return (
    <Card
      title={<TitleWithSource metric="activityTrend">Activity over time</TitleWithSource>}
      subtitle={
        <>
          {subtitle ? `${subtitle} · ` : ""}
          {formatDate(range.from, { year: false })} – {formatDate(range.to)}
          {usingOwn ? "" : " · page period"}
        </>
      }
      actions={
        <div className="bd-trend-range">
          <select
            className="bd-select bd-trend-range__preset"
            value={preset}
            onChange={(e) => {
              const id = e.target.value;
              if (id === "custom") {
                setCustom((c) => c || { from: draft.from, to: draft.to });
              }
              setPreset(id);
            }}
            aria-label="Chart date range"
          >
            <option value={FOLLOW_PAGE}>Page period</option>
            {PRESETS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
          {preset === "custom" && (
            <div className="bd-trend-range__custom">
              <input type="date" className="bd-input" value={draft.from} max={draft.to} onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))} aria-label="From" />
              <span className="bd-muted">–</span>
              <input type="date" className="bd-input" value={draft.to} min={draft.from} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} aria-label="To" />
              <button type="button" className="bd-btn bd-btn--primary" disabled={!isIso(draft.from) || !isIso(draft.to)} onClick={applyCustom}>
                Apply
              </button>
            </div>
          )}
        </div>
      }
    >
      {busy ? (
        <Skeleton height={height} />
      ) : usingOwn && own.error ? (
        <p className="bd-muted">{own.error}</p>
      ) : (
        <TrendChart trend={trend} granularity={granularity} height={height} />
      )}
    </Card>
  );
}
